/**
 * Register Management grid — pure presentation rules (owner block, mode, filters, row actions).
 *
 * Approved design: src/assets/showcase/register-mode-visibility-showcase.html
 *   #surface=polish, Direction A "Calm flat table", Owner A "icon + two lines", list data "mode only".
 *
 * No Vue / Vuetify imports: unit-tested with `node --test`.
 */
import { isGroupRegister, FACILITY_MODE } from './registerImportWizardModel.js';

/** Toolbar owner-type segments. */
export const OWNER_TYPE = Object.freeze({ ALL: 'all', FACILITY: 'facility', GROUP: 'group' });

/** Toolbar Mode filter values. */
export const MODE_FILTER = Object.freeze({
  ALL: 'all',
  SHARED: FACILITY_MODE.SHARED,
  SEPARATED: FACILITY_MODE.SEPARATED,
  UNSET: 'unset',
});

export const MODE_FILTER_OPTIONS = Object.freeze([
  { text: 'All modes', value: MODE_FILTER.ALL },
  { text: 'Shared', value: MODE_FILTER.SHARED },
  { text: 'Separated', value: MODE_FILTER.SEPARATED },
  { text: 'Mode not set', value: MODE_FILTER.UNSET },
]);

export const OWNER_KIND = Object.freeze({ FACILITY: 'facility', GROUP: 'group', NONE: 'none' });

const OWNER_KIND_META = Object.freeze({
  [OWNER_KIND.FACILITY]: { icon: 'mdi-office-building-outline', label: 'Facility' },
  [OWNER_KIND.GROUP]: { icon: 'mdi-sitemap-outline', label: 'Facility Group' },
  [OWNER_KIND.NONE]: { icon: '', label: '' },
});

/**
 * Mode copy — same wording and icons as the import wizard. The list API returns only
 * `facilityAssignmentMode` (no row count), so a Facility Group register without a mode cannot tell
 * "empty" from "has rows but no mode": it renders the neutral "unknown" state, not the amber
 * "unset" warning (showcase `modeState(r, false)`). `tone` maps to a CSS modifier.
 */
export const MODE_PRESENTATION = Object.freeze({
  [FACILITY_MODE.SHARED]: {
    key: FACILITY_MODE.SHARED,
    icon: 'mdi-share-variant-outline',
    label: 'Shared',
    description: 'Every Facility in the Facility Group sees all rows',
    tone: 'set',
  },
  [FACILITY_MODE.SEPARATED]: {
    key: FACILITY_MODE.SEPARATED,
    icon: 'mdi-call-split',
    label: 'Separated',
    description: 'Each Facility sees only its rows',
    tone: 'set',
  },
  unknown: {
    key: 'unknown',
    icon: 'mdi-help-circle-outline',
    label: 'Mode not set',
    description: 'Shared or Separated is set by the first import',
    tone: 'pending',
  },
});

const EMPTY = '—';

function registerFacilityGroupId(register) {
  return register?.facilityGroupId || register?.facilityGroup?.id || null;
}

function registerFacilityId(register) {
  return register?.facilityId || register?.facility?.id || null;
}

/**
 * Owner kind. A Facility Group register is exactly what the API maps a mode for
 * (`facilityGroupId` set, `facilityId` empty — RegisterVM.FacilityAssignmentMode).
 */
export function registerOwnerKind(register) {
  if (!register) return OWNER_KIND.NONE;
  if (isGroupRegister(register) || (!register.facilityId && register.facilityGroup?.id)) return OWNER_KIND.GROUP;
  if (registerFacilityId(register)) return OWNER_KIND.FACILITY;
  return OWNER_KIND.NONE;
}

function ownerName(register, kind) {
  if (kind === OWNER_KIND.GROUP) return register.facilityGroup?.name || register.facilityGroupName || EMPTY;
  if (kind === OWNER_KIND.FACILITY) return register.facility?.name || register.facilityName || EMPTY;
  return EMPTY;
}

/** The mode block for a Facility Group register; null for any other owner. */
export function registerModePresentation(register) {
  if (registerOwnerKind(register) !== OWNER_KIND.GROUP) return null;
  return MODE_PRESENTATION[register.facilityAssignmentMode] || MODE_PRESENTATION.unknown;
}

/**
 * Two-line owner block: line 1 icon + name, line 2 "Facility" or "Facility Group · <mode>".
 * @returns {{ kind: string, name: string, icon: string, kindLabel: string, mode: ?object }}
 */
export function registerOwnerPresentation(register) {
  const kind = registerOwnerKind(register);
  const meta = OWNER_KIND_META[kind];
  return {
    kind,
    name: ownerName(register || {}, kind),
    icon: meta.icon,
    kindLabel: meta.label,
    mode: registerModePresentation(register),
  };
}

/** Owner-type segment + owner select. An empty owner id matches every register of that type. */
export function matchesOwnerFilter(register, ownerType, ownerId) {
  if (!ownerType || ownerType === OWNER_TYPE.ALL) return true;
  const kind = registerOwnerKind(register);
  if (ownerType === OWNER_TYPE.FACILITY) {
    return kind === OWNER_KIND.FACILITY && (!ownerId || registerFacilityId(register) === ownerId);
  }
  if (ownerType === OWNER_TYPE.GROUP) {
    return kind === OWNER_KIND.GROUP && (!ownerId || registerFacilityGroupId(register) === ownerId);
  }
  return true;
}

/** Mode filter. Any value other than "all" keeps Facility Group registers only. */
export function matchesModeFilter(register, filter) {
  if (!filter || filter === MODE_FILTER.ALL) return true;
  const mode = registerModePresentation(register);
  if (!mode) return false;
  if (filter === MODE_FILTER.UNSET) return mode === MODE_PRESENTATION.unknown;
  return mode.key === filter;
}

/**
 * Params for FETCH_REGISTERS. Only a selected Facility is filtered server-side (as before);
 * Facility Group and "All" load the full list and filter on the client.
 */
export function registerFetchParams(ownerType, ownerId) {
  if (ownerType === OWNER_TYPE.FACILITY && ownerId) return { facilityId: ownerId };
  return {};
}

/** Owner select placeholder per owner type. */
export function ownerSelectPlaceholder(ownerType) {
  if (ownerType === OWNER_TYPE.FACILITY) return 'All Facilities';
  if (ownerType === OWNER_TYPE.GROUP) return 'All Facility Groups';
  return 'All owners';
}

export function registerCountLabel(count) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? 'register' : 'registers'}`;
}

/* ---------------------------------------------------------------------------------------------
 * Type / Status dots — Vuetify colour classes (no new hex values).
 * ------------------------------------------------------------------------------------------- */

const TYPE_COLOR_CLASS = Object.freeze({
  equipment: 'teal',
  pipeline: 'deep-purple',
  structural: 'brown',
  inspection: 'orange',
  general: 'indigo',
  anomaly: 'red darken-2',
  avainspect: 'cyan darken-2',
  avainspection: 'cyan darken-2',
  scaffold: 'blue-grey',
  permit: 'deep-orange',
  container: 'purple',
  operation: 'primary',
});

/** @param {string} typeText - display text or raw string type ("AVA Inspection", "pipeline"). */
export function registerTypeColorClass(typeText) {
  const key = String(typeText || '').toLowerCase().replace(/\s+/g, '');
  return TYPE_COLOR_CLASS[key] || 'primary';
}

const STATUS_DOT_CLASS = Object.freeze({ active: 'success', draft: 'golden' });

/** Vuetify colour class for the status dot; null renders a hollow dot (Inactive / unknown). */
export function registerStatusDotClass(statusText) {
  return STATUS_DOT_CLASS[String(statusText || '').toLowerCase()] || null;
}

/* ---------------------------------------------------------------------------------------------
 * Row actions. Every action and its handler are the same as before the polish; only placement
 * changed. `permission` is a key of permissions.Registers (null = always shown). "Clear all data"
 * needs Registers.Delete, like the API's DELETE clear-all (AVA-602 security, API a4929ece).
 * ------------------------------------------------------------------------------------------- */

export const REGISTER_ROW_ACTIONS = Object.freeze([
  { key: 'viewData', icon: 'mdi-table-eye', label: 'View data', method: 'viewRegisterData', permission: null, placement: 'primary' },
  { key: 'importExcel', icon: 'mdi-file-excel-outline', label: 'Import from Excel', method: 'uploadExcel', permission: 'Import', placement: 'primary' },
  { key: 'edit', icon: 'mdi-pencil-outline', label: 'Edit register', method: 'editRegister', permission: 'Update', placement: 'primary' },
  { key: 'relationships', icon: 'mdi-vector-link', label: 'Manage relationships', method: 'openRegisterRelationships', permission: null, placement: 'menu' },
  { key: 'apiDataSources', icon: 'mdi-api', label: 'API data sources', method: 'openApiDataSources', permission: 'Import', placement: 'menu' },
  { key: 'updateSchema', icon: 'mdi-table-cog', label: 'Update schema', method: 'updateSchema', permission: 'Update', placement: 'menu' },
  { key: 'export', icon: 'mdi-tray-arrow-down', label: 'Export register', method: 'exportRegister', permission: 'Export', placement: 'menu' },
  { key: 'auditHistory', icon: 'mdi-history', label: 'View audit history', method: 'openRegisterAuditHistory', permission: null, placement: 'menu' },
  { key: 'clearData', icon: 'mdi-broom', label: 'Clear all data…', method: 'clearRegisterData', permission: 'Delete', placement: 'menu', danger: true },
  { key: 'delete', icon: 'mdi-delete-outline', label: 'Delete register…', method: 'deleteRegister', permission: 'Delete', placement: 'menu', danger: true },
]);

/**
 * @param {(permissionKey: string) => boolean} can - resolves a permissions.Registers key.
 * @returns {{ primary: object[], menu: object[], danger: object[] }} menu = safe items, danger last.
 */
export function resolveRegisterRowActions(can) {
  const allowed = REGISTER_ROW_ACTIONS.filter((a) => !a.permission || !!(can && can(a.permission)));
  return {
    primary: allowed.filter((a) => a.placement === 'primary'),
    menu: allowed.filter((a) => a.placement === 'menu' && !a.danger),
    danger: allowed.filter((a) => a.placement === 'menu' && a.danger),
  };
}

/* ---------------------------------------------------------------------------------------------
 * "Clear all data" failure copy. DELETE clear-all needs Registers.Delete plus owner access
 * (AVA-602 security, API a4929ece), so a user who sees the action can still get a 403. Fixed copy
 * only; the server's text is never shown.
 * ------------------------------------------------------------------------------------------- */

export const CLEAR_REGISTER_DATA_ERROR_COPY = Object.freeze({
  forbidden: "You don't have permission to clear this register's data.",
  failed: 'Failed to clear register data',
});

const HTTP_FORBIDDEN = 403;

/** @param {unknown} err axios error from the clear-all request */
export function clearRegisterDataErrorMessage(err) {
  return err?.response?.status === HTTP_FORBIDDEN
    ? CLEAR_REGISTER_DATA_ERROR_COPY.forbidden
    : CLEAR_REGISTER_DATA_ERROR_COPY.failed;
}
